import { Suspense, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";

import Loading from "@/common/components/ui/loading/Loading";
import { formatDateAndTime } from "@/common/helpers/date.helpers";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { getRenters } from "@/common/services/renters.api";
import { IRenter, RENTER_STATUS, RenterType } from "@/types/renter.type";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import Pendencies from "@/modules/renters/components/Pendencies";
import { STATUS_LABELS } from "@/modules/vehicles/components/TableVehicles";
import ModalCreateRenterB2B from "../modals/CreateRenterB2B";
import ModalEditRenter from "@/modules/renters/components/modals/EditRenter";
import { getStatusLabelsRenters } from "@/modules/renters/components/table-renters/TableRenters";
import ModalApproveRenter from "@/modules/renters/components/modals/ApproveRenter";
import { getActiveContractB2BPlates } from "@/common/services/contracts-b2b.api";

type RenterActionData = RenterType & { idCompanyB2B: string };

type ActionItem = {
  label: string;
  action: (data: unknown) => void;
  disabled: (data: unknown) => boolean;
  permission?: string;
};

const EMPTY_ACTION = { eventName: "", data: undefined };

export default function TableRenterB2B() {
  const { id: idCompanyB2B } = useParams<{ id: string }>();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [allowedPlates, setAllowedPlates] = useState<string[] | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [renters, setRenters] = useState<IRenter[]>([]);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "name,cpf,email,cnh",
    orderBy: "-updatedAt",
  });

  const B2B_ACTIONS: Omit<ActionItem, "action">[] = [
    {
      label: "Editar",
      disabled: () => false,
      permission: "edit:renters",
    },
    {
      label: "Aprovar cadastro",
      disabled: (data) =>
        RENTER_STATUS.ProntoParaAprovacao !== (data as RenterType).status,
      permission: "approve:renters",
    },
  ];

  const loadRenters = useCallback(
    async (currentFilters: TableFiltersState, currentPage: number) => {
      if (!idCompanyB2B) return;

      setIsLoading(true);
      setRenters([]);
      const filtersCopy: TableFiltersState & {
        isB2Bcontrat?: string;
        idCompanyB2B?: string;
        customFilters?: string;
      } = { ...currentFilters };

      if (filtersCopy.status.length === 0) {
        filtersCopy.status = [
          RENTER_STATUS.Ativo,
          RENTER_STATUS.ProntoParaAprovacao,
          RENTER_STATUS.PendentePagamento,
          RENTER_STATUS.ReservaPaga,
          RENTER_STATUS.ReservaPendentePagamento,
          RENTER_STATUS.Congelado,
          RENTER_STATUS.Indefinido,
        ];
      }

      const result = await getRenters({
        ...filtersCopy,
        page: currentPage,
        idCompanyB2B: idCompanyB2B,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);

      setData({
        itemsPerPage: filtersCopy.itemsPerPage
          ? Number(filtersCopy.itemsPerPage)
          : 10,
        totalItems: result.totalItems,
      });

      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message ||
          "Erro ao carregar clientes B2B"
        );
        return;
      }
      setRenters(result.data || []);
      setIsLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idCompanyB2B]
  );

  useEffect(() => {
    async function loadAllowedPlates() {
      if (!idCompanyB2B) {
        setAllowedPlates([]);
        return;
      }

      const result = await getActiveContractB2BPlates(idCompanyB2B);

      if (result.ok && result.data) {
        setAllowedPlates(result.data.licensePlates);
      } else {
        notifyError(result.message || "Falha ao carregar placas permitidas.");
        setAllowedPlates([]);
      }
    }
    loadAllowedPlates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCompanyB2B]);


  useEffect(() => {
    loadRenters(filters, page);
  }, [filters, page, idCompanyB2B, loadRenters]);

  const isAction = (eventName: string) => action.eventName === eventName;

  const cleanActionData = (newRenterId?: string) => {
    loadRenters(filters, page);
    if (newRenterId) {
      setAction({ eventName: "editB2B", data: newRenterId });
    } else {
      setAction(EMPTY_ACTION);
    }
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
    setPage(1);
  };

  const actionsWithB2BContext: ActionItem[] = B2B_ACTIONS.map((actionItem) => ({
    ...actionItem,
    action: (data) => {
      let eventName = "";
      let actionData: unknown = data;

      if (actionItem.label.includes("Aprovar")) {
        eventName = "approveB2B";
        actionData = { ...(data as RenterType), idCompanyB2B };
      } else if (actionItem.label === "Editar") {
        eventName = "editB2B";
        actionData = (data as RenterType)._id;
      }

      setAction({ eventName, data: actionData });
    },
    disabled: actionItem.disabled as (data: unknown) => boolean,
  }));

  const B2B_HEADERS = {
    companyId: "Cidade",
    name: "Nome",
    cpf: "CPF",
    pendencies: "Pendências",
    status: "Status",
    updatedAt: "Atualizado em",
    createdAt: "Criado em",
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section p-4">
        <TableFilters
          dashboardHeader={`Clientes B2B (${data.totalItems})`}
          placeholder="Digite o nome ou CPF do locatário."
          statusOptions={STATUS_LABELS}
          newItemLabel="Novo Cliente B2B"
          onNewItemClick={() =>
            setAction({ eventName: "createB2B", data: idCompanyB2B })
          }
          onSearch={handleFilterChange}
          entity="renters"
          tableData={renters as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          headers={B2B_HEADERS}
          data={renters}
          actions={actionsWithB2BContext}
          setFilters={({ orderBy }) => {
            handleFilterChange({ orderBy });
          }}
          renders={
            {
              companyId: (companyId) => {
                return getCompanySigla(companyId as string) || "";
              },
              name: (name) => (
                <span className="font-semibold">{name as string}</span>
              ),
              cpf: (cpf) => cpf || "-",
              pendencies: (_, renter) => (
                <Pendencies renter={renter as RenterType} />
              ),
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              status: getStatusLabelsRenters,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="renters"
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />

        {isAction("createB2B") && (
          <ModalCreateRenterB2B
            idCompanyB2B={action.data as string}
            onClose={cleanActionData}
            onSave={() => cleanActionData()}
          />
        )}
        {isAction("editB2B") && (
          <ModalEditRenter
            renterId={action.data as string}
            onClose={cleanActionData}
          />
        )}
        {isAction("approveB2B") && (
          <ModalApproveRenter
            renter={action.data as RenterActionData}
            onClose={cleanActionData}
            allowedPlates={allowedPlates}
            idCompanyB2B={idCompanyB2B}
          />
        )}
      </div>
    </Suspense>
  );
}
